import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { usersDB } from '../lib/db/users';
import { schoolsDB } from '../lib/db/schools';
import { leagueBadgePath } from '../lib/shurikens';
import LoadingSpinner from '../components/common/LoadingSpinner';

export default function Leaderboard() {
  const [entries, setEntries] = useState([]);
  const [lastChampion, setLastChampion] = useState(null);
  const [currentUserRank, setCurrentUserRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // Get current user's schoolId
        const userData = await usersDB.getUserData(currentUser.uid);
        const schoolId = userData?.profile?.schoolId;
        console.log('[Leaderboard] userData.profile.schoolId =', schoolId);
        if (!schoolId) {
          setError(new Error('No school found'));
          return;
        }

        // Read pre-computed leaderboard from school subcollection
        const leaderboardRef = collection(db, 'schools', schoolId, 'leaderboard');
        console.log('[Leaderboard] fetching leaderboard subcollection...');
        const snapshot = await getDocs(leaderboardRef);
        console.log('[Leaderboard] getDocs returned', snapshot.docs.length, 'entries');

        const raw = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || '?',
          weekly: doc.data().weekly || 0,
          total: doc.data().total || 0,
          league: doc.data().league || 'lead',
          isWeeklyChampion: doc.data().isWeeklyChampion || false,
          lastKnownRank: doc.data().lastKnownRank ?? null,
        }));

        // Sort: weekly DESC, then lastKnownRank ASC for tiebreak
        const sorted = raw.sort((a, b) => {
          const diff = b.weekly - a.weekly;
          if (diff !== 0) return diff;
          const aRank = a.lastKnownRank ?? 9999;
          const bRank = b.lastKnownRank ?? 9999;
          return aRank - bRank;
        });

        setEntries(sorted);
        const myRank = sorted.findIndex(e => e.id === currentUser.uid) + 1;
        setCurrentUserRank(myRank || null);

        // Get school doc for last week's champion
        const school = await schoolsDB.getSchool(schoolId);
        if (school?.lastWeeklyChampion?.name) {
          setLastChampion(school.lastWeeklyChampion);
        }
      } catch (err) {
        console.error('Error loading leaderboard:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUser]);

  if (loading) return <LoadingSpinner message={t('common.loading')} />;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t('error.something_went_wrong')}</p>
          <button onClick={() => navigate('/home')} className="text-blue-600 underline">
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  const medalFor = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-lg shadow-md border-b border-gray-100">
        <div className="px-4 md:max-w-4xl md:mx-auto md:px-6 py-4 md:py-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center text-xl"
          >
            ←
          </button>
          <h1 className="font-bold text-xl md:text-3xl text-gray-800">
            {t('leaderboard.title')}
          </h1>
          {currentUserRank && (
            <span className="ms-auto text-sm md:text-base text-gray-500 font-medium">
              {t('leaderboard.your_rank', { rank: currentUserRank })}
            </span>
          )}
        </div>
      </div>

      <div className="px-4 py-4 md:max-w-4xl md:mx-auto md:px-6 md:py-8 space-y-4">
        {/* Last week's champion banner */}
        {lastChampion && (
          <div className="bg-gradient-to-r from-yellow-400 to-amber-500 rounded-2xl p-4 md:p-6 text-white shadow-lg">
            <div className="text-sm md:text-base font-semibold opacity-90">
              {t('leaderboard.last_week_champion', { name: lastChampion.name })}
            </div>
            <div className="text-xs md:text-sm opacity-75 mt-0.5">
              {lastChampion.shurikens} ⭐ · {lastChampion.weekOf}
            </div>
          </div>
        )}

        {/* Weekly leaderboard */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-100">
            <h2 className="font-bold text-base md:text-xl text-gray-700">
              {t('leaderboard.weekly')}
            </h2>
          </div>

          {entries.length === 0 ? (
            <div className="p-6 text-center text-gray-500">{t('home.no_recent_trainings')}</div>
          ) : (
            <ul>
              {entries.slice(0, 10).map((entry, idx) => {
                const rank = idx + 1;
                const isMe = entry.id === currentUser.uid;
                const medal = medalFor(rank);

                return (
                  <li
                    key={entry.id}
                    className={`flex items-center gap-3 px-4 md:px-6 py-3 md:py-4 border-b border-gray-50 last:border-b-0 transition-colors
                      ${isMe ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    {/* Rank */}
                    <div className="w-8 text-center font-bold text-gray-500 text-sm md:text-base shrink-0">
                      {medal || `#${rank}`}
                    </div>

                    {/* League badge */}
                    <img
                      src={leagueBadgePath(entry.league)}
                      alt={entry.league}
                      className="w-6 h-6 md:w-8 md:h-8 object-contain shrink-0"
                    />

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm md:text-base truncate ${isMe ? 'text-blue-700' : 'text-gray-800'}`}>
                        {entry.name}
                        {isMe && (
                          <span className="ms-2 text-xs text-blue-400 font-normal">({t('common.you', 'you')})</span>
                        )}
                        {entry.isWeeklyChampion && (
                          <span className="ms-2 text-base" title="Weekly Champion">🏆</span>
                        )}
                      </div>
                    </div>

                    {/* Weekly shurikens */}
                    <div className="flex items-center gap-1 shrink-0">
                      <img
                        src={entry.weekly >= 10 ? '/shurikan-gold.png' : '/shurikan-silver.png'}
                        alt="shuriken"
                        className="w-5 h-5 md:w-6 md:h-6 object-contain"
                      />
                      <span className="font-bold text-sm md:text-base text-gray-700">{entry.weekly}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
